import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Trash2, Edit, Eye, Save, Settings, MessageSquare, Briefcase, X, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { ImageUpload } from '@/components/ImageUpload';
import { resolveImageUrl } from '@/utils/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = ['Home', 'Hammer', 'Layers', 'Triangle', 'Maximize2', 'Shield', 'Droplets', 'Flame', 'Leaf', 'Wrench', 'Sun', 'Zap', 'Building'];

function createApi() {
  const token = localStorage.getItem('hke_admin_token');
  const instance = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` }
  });
  instance.interceptors.response.use(r => r, err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('hke_admin_token');
      window.location.href = '/admin';
    }
    return Promise.reject(err);
  });
  return instance;
}

/* ---- SERVICES TAB ---- */
function ServicesTab() {
  const api = createApi();
  const [services, setServices] = useState([]);
  const [editService, setEditService] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get('/admin/services');
    setServices(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditService({ title: '', description: '', icon: 'Home', order: services.length + 1, active: true });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditService({ ...s });
    setDialogOpen(true);
  };

  const saveService = async () => {
    if (!editService.title) { toast.error('Titel ist erforderlich'); return; }
    try {
      if (editService.id) {
        await api.put(`/admin/services/${editService.id}`, editService);
        toast.success('Leistung aktualisiert');
      } else {
        await api.post('/admin/services', editService);
        toast.success('Leistung erstellt');
      }
      setDialogOpen(false);
      load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Leistung wirklich löschen?')) return;
    await api.delete(`/admin/services/${id}`);
    toast.success('Gelöscht');
    load();
  };

  const toggleActive = async (s) => {
    await api.put(`/admin/services/${s.id}`, { active: !s.active });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-bold text-slate-900 uppercase">Leistungen ({services.length})</h2>
        <Button onClick={openNew} className="bg-[#0077D4] hover:bg-[#005BA8] text-white rounded-none" data-testid="add-service-button">
          <Plus size={16} className="mr-2" /> Neue Leistung
        </Button>
      </div>

      <div className="space-y-3">
        {services.map(s => (
          <div key={s.id} className="bg-white border border-slate-200 p-4 flex items-center justify-between gap-4" data-testid={`admin-service-${s.id}`}>
            <div className="flex items-center gap-4 min-w-0">
              <span className="text-xs text-slate-400 w-6 flex-shrink-0">#{s.order}</span>
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{s.title}</p>
                <p className="text-xs text-slate-500 truncate">{s.description?.substring(0, 80)}...</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`toggle-service-${s.id}`} />
              <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}>
                <Edit size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => deleteService(s.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-service-${s.id}`}>
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase">{editService?.id ? 'Leistung bearbeiten' : 'Neue Leistung'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Titel, Beschreibung und Icon der Leistung bearbeiten.</DialogDescription>
          </DialogHeader>
          {editService && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm">Titel</Label>
                <Input value={editService.title} onChange={e => setEditService({ ...editService, title: e.target.value })} className="mt-1 rounded-none" data-testid="service-title-input" />
              </div>
              <div>
                <Label className="text-sm">Beschreibung</Label>
                <Textarea value={editService.description} onChange={e => setEditService({ ...editService, description: e.target.value })} rows={3} className="mt-1 rounded-none" data-testid="service-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Icon</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {ICONS.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setEditService({ ...editService, icon })}
                        className={`px-3 py-1 text-xs border ${editService.icon === icon ? 'bg-[#0077D4] text-white border-[#0077D4]' : 'border-slate-300 text-slate-600 hover:border-[#0077D4]'}`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Reihenfolge</Label>
                  <Input type="number" value={editService.order} onChange={e => setEditService({ ...editService, order: parseInt(e.target.value) || 0 })} className="mt-1 rounded-none" data-testid="service-order-input" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Abbrechen</Button>
            <Button onClick={saveService} className="bg-[#0077D4] hover:bg-[#005BA8] text-white rounded-none" data-testid="save-service-button">
              <Save size={16} className="mr-2" /> Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- CONTACTS TAB ---- */
function ContactsTab() {
  const api = createApi();
  const [contacts, setContacts] = useState([]);
  const [viewContact, setViewContact] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get('/admin/contacts');
    setContacts(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.put(`/admin/contacts/${id}/read`);
    load();
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success('Gelöscht');
    setViewContact(null);
    load();
  };

  const unreadCount = contacts.filter(c => !c.read).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-bold text-slate-900 uppercase">
          Nachrichten ({contacts.length})
          {unreadCount > 0 && <Badge className="ml-3 bg-[#CC2229] text-white rounded-none">{unreadCount} neu</Badge>}
        </h2>
      </div>

      {contacts.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="no-contacts">
          <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
          Noch keine Nachrichten eingegangen
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map(c => (
            <div
              key={c.id}
              className={`bg-white border p-4 flex items-center justify-between gap-4 cursor-pointer hover:border-[#0077D4] transition-colors ${c.read ? 'border-slate-200' : 'border-[#CC2229] border-l-4'}`}
              onClick={() => { setViewContact(c); if (!c.read) markRead(c.id); }}
              data-testid={`contact-item-${c.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{c.name}</p>
                  {!c.read && <Badge className="bg-[#CC2229] text-white text-xs rounded-none">Neu</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-1">{c.email} {c.service && `· ${c.service}`}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString('de-DE')}</span>
                <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); deleteContact(c.id); }} className="text-red-500">
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!viewContact} onOpenChange={() => setViewContact(null)}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase">Nachricht von {viewContact?.name}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Kontaktanfrage Details</DialogDescription>
          </DialogHeader>
          {viewContact && (
            <div className="space-y-3 py-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-slate-400 text-xs">E-Mail</p><p className="font-medium">{viewContact.email}</p></div>
                <div><p className="text-slate-400 text-xs">Telefon</p><p className="font-medium">{viewContact.phone || '—'}</p></div>
                <div><p className="text-slate-400 text-xs">Leistung</p><p className="font-medium">{viewContact.service || '—'}</p></div>
                <div><p className="text-slate-400 text-xs">Datum</p><p className="font-medium">{new Date(viewContact.created_at).toLocaleString('de-DE')}</p></div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-slate-400 text-xs mb-2">Nachricht</p>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{viewContact.message}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => deleteContact(viewContact?.id)} className="text-red-500 rounded-none" data-testid="delete-contact-dialog">
              <Trash2 size={16} className="mr-2" /> Löschen
            </Button>
            <Button onClick={() => setViewContact(null)} className="bg-[#0077D4] text-white rounded-none">Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- SETTINGS TAB ---- */
function SettingsTab() {
  const api = createApi();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then(res => setSettings(res.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Einstellungen gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (!settings) return <div className="p-8 text-center text-slate-400">Laden...</div>;

  const Field = ({ label, field, type = 'text', rows }) => (
    <div>
      <Label className="text-sm text-slate-600">{label}</Label>
      {rows ? (
        <Textarea value={settings[field] || ''} onChange={e => update(field, e.target.value)} rows={rows} className="mt-1 rounded-none bg-slate-50" data-testid={`settings-${field}`} />
      ) : (
        <Input type={type} value={settings[field] || ''} onChange={e => update(field, e.target.value)} className="mt-1 rounded-none bg-slate-50 h-11" data-testid={`settings-${field}`} />
      )}
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Logo & Hero */}
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-wider mb-6">Logo & Hero</h3>
        <div className="space-y-4">
          <ImageUpload
            value={settings.logo_url}
            onChange={v => update('logo_url', v)}
            label="Logo"
          />
          <Field label="Hero Überschrift (leer = Standard-Design)" field="hero_headline" />
          <Field label="Hero Unterüberschrift" field="hero_subheadline" />
          <Field label="Hero Button-Text" field="hero_cta_text" />
        </div>
      </div>

      {/* Company */}
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-wider mb-6">Unternehmen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Firmenname" field="company_name" />
          <Field label="Zusatz" field="company_subtitle" />
          <Field label="Adresse" field="address" />
          <Field label="Telefon" field="phone" />
          <Field label="Mobil" field="mobile" />
          <Field label="E-Mail" field="email" />
          <div className="md:col-span-2">
            <Field label="Öffnungszeiten" field="opening_hours" rows={2} />
          </div>
        </div>
      </div>

      {/* VELUX */}
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-wider mb-6">VELUX Partner</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Switch checked={settings.velux_active || false} onCheckedChange={v => update('velux_active', v)} data-testid="settings-velux_active" />
            <Label className="text-sm">VELUX Bereich anzeigen</Label>
          </div>
          <Field label="VELUX Titel" field="velux_title" />
          <Field label="VELUX Beschreibung" field="velux_description" rows={2} />
          <Field label="VELUX Konfigurator Link" field="velux_link" />
          {settings.velux_link && (
            <a href={settings.velux_link} target="_blank" rel="noopener noreferrer" className="text-sm text-[#0077D4] hover:underline">
              Link testen &rarr;
            </a>
          )}
        </div>
      </div>

      {/* About */}
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-wider mb-6">Über uns</h3>
        <div className="space-y-4">
          <Field label="Überschrift" field="about_headline" />
          <Field label="Text" field="about_text" rows={6} />
        </div>
      </div>

      {/* Save */}
      <div className="sticky bottom-0 bg-slate-50 py-4 border-t border-slate-200">
        <Button onClick={save} disabled={saving} className="bg-[#CC2229] hover:bg-[#a31b20] text-white rounded-none px-10 py-3 h-auto font-bold uppercase tracking-wider" data-testid="save-settings-button">
          <Save size={18} className="mr-2" /> {saving ? 'Speichern...' : 'Alle Einstellungen speichern'}
        </Button>
      </div>
    </div>
  );
}

/* ---- REFERENCES TAB ---- */
function ReferencesTab() {
  const api = createApi();
  const [refs, setRefs] = useState([]);
  const [editRef, setEditRef] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get('/admin/references');
    setRefs(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditRef({ title: '', description: '', image_url: '', category: '', year: new Date().getFullYear().toString(), location: '', active: true, order: refs.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (r) => { setEditRef({ ...r }); setDialogOpen(true); };

  const saveRef = async () => {
    if (!editRef.title) { toast.error('Titel ist erforderlich'); return; }
    try {
      if (editRef.id) {
        await api.put(`/admin/references/${editRef.id}`, editRef);
        toast.success('Referenz aktualisiert');
      } else {
        await api.post('/admin/references', editRef);
        toast.success('Referenz erstellt');
      }
      setDialogOpen(false);
      load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const deleteRef = async (id) => {
    if (!window.confirm('Referenz wirklich löschen?')) return;
    await api.delete(`/admin/references/${id}`);
    toast.success('Gelöscht');
    load();
  };

  const toggleActive = async (r) => {
    await api.put(`/admin/references/${r.id}`, { active: !r.active });
    load();
  };

  const upd = (key, val) => setEditRef(prev => ({ ...prev, [key]: val }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-bold text-slate-900 uppercase">Referenzen ({refs.length})</h2>
        <Button onClick={openNew} className="bg-[#0077D4] hover:bg-[#005BA8] text-white rounded-none" data-testid="add-reference-button">
          <Plus size={16} className="mr-2" /> Neue Referenz
        </Button>
      </div>

      <div className="space-y-3">
        {refs.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 p-4 flex items-center gap-4" data-testid={`admin-ref-${r.id}`}>
            {r.image_url && <img src={resolveImageUrl(r.image_url)} alt="" className="w-20 h-14 object-cover flex-shrink-0 border" />}
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-900 truncate">{r.title}</p>
              <p className="text-xs text-slate-500">{r.category} · {r.location} · {r.year}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} />
              <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Edit size={16} /></Button>
              <Button variant="ghost" size="sm" onClick={() => deleteRef(r.id)} className="text-red-500"><Trash2 size={16} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase">{editRef?.id ? 'Referenz bearbeiten' : 'Neue Referenz'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Projektdetails eingeben.</DialogDescription>
          </DialogHeader>
          {editRef && (
            <div className="space-y-4 py-4">
              <div><Label className="text-sm">Titel</Label><Input value={editRef.title} onChange={e => upd('title', e.target.value)} className="mt-1 rounded-none" data-testid="ref-title-input" /></div>
              <div><Label className="text-sm">Beschreibung</Label><Textarea value={editRef.description} onChange={e => upd('description', e.target.value)} rows={3} className="mt-1 rounded-none" /></div>
              <ImageUpload value={editRef.image_url} onChange={v => upd('image_url', v)} label="Projektbild" />
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-sm">Kategorie</Label><Input value={editRef.category} onChange={e => upd('category', e.target.value)} className="mt-1 rounded-none" /></div>
                <div><Label className="text-sm">Jahr</Label><Input value={editRef.year} onChange={e => upd('year', e.target.value)} className="mt-1 rounded-none" /></div>
                <div><Label className="text-sm">Ort</Label><Input value={editRef.location} onChange={e => upd('location', e.target.value)} className="mt-1 rounded-none" /></div>
              </div>
              <div><Label className="text-sm">Reihenfolge</Label><Input type="number" value={editRef.order} onChange={e => upd('order', parseInt(e.target.value) || 0)} className="mt-1 rounded-none w-24" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Abbrechen</Button>
            <Button onClick={saveRef} className="bg-[#0077D4] hover:bg-[#005BA8] text-white rounded-none" data-testid="save-reference-button"><Save size={16} className="mr-2" /> Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- MAIN DASHBOARD ---- */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('hke_admin_token');

  useEffect(() => {
    if (!token) navigate('/admin');
  }, [token, navigate]);

  const logout = () => {
    localStorage.removeItem('hke_admin_token');
    navigate('/admin');
    toast.success('Abgemeldet');
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0077D4] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-heading text-lg font-bold uppercase tracking-wider">HKE Admin</span>
            <a href="/" target="_blank" className="text-xs text-white/50 hover:text-white/80 transition-colors" data-testid="admin-view-site">
              Webseite ansehen &rarr;
            </a>
          </div>
          <Button variant="ghost" onClick={logout} className="text-white/70 hover:text-white hover:bg-white/10 rounded-none" data-testid="admin-logout-button">
            <LogOut size={18} className="mr-2" /> Abmelden
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="services">
          <TabsList className="bg-white border border-slate-200 rounded-none h-12 mb-8 p-1" data-testid="admin-tabs">
            <TabsTrigger value="services" className="rounded-none font-medium data-[state=active]:bg-[#0077D4] data-[state=active]:text-white px-6" data-testid="tab-services">
              <Briefcase size={16} className="mr-2" /> Leistungen
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none font-medium data-[state=active]:bg-[#0077D4] data-[state=active]:text-white px-6" data-testid="tab-contacts">
              <MessageSquare size={16} className="mr-2" /> Nachrichten
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none font-medium data-[state=active]:bg-[#0077D4] data-[state=active]:text-white px-6" data-testid="tab-settings">
              <Settings size={16} className="mr-2" /> Einstellungen
            </TabsTrigger>
            <TabsTrigger value="references" className="rounded-none font-medium data-[state=active]:bg-[#0077D4] data-[state=active]:text-white px-6" data-testid="tab-references">
              <Image size={16} className="mr-2" /> Referenzen
            </TabsTrigger>
          </TabsList>

          <TabsContent value="services"><ServicesTab /></TabsContent>
          <TabsContent value="contacts"><ContactsTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
          <TabsContent value="references"><ReferencesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
